/**
 * Ingredient units normalization.
 *
 * Each recognized unit belongs to a FAMILY with a base unit:
 *   - mass   → grams (g)
 *   - volume → milliliters (ml)
 *   - spoon_small / spoon_big → teaspoons / tablespoons (do not convert to each other)
 *   - count  → units (pcs); also the family of ingredients without units
 *
 * Quantities are only summed within the SAME family; distinct families for
 * the same ingredient produce separate lines on the shopping list.
 */

export type UnitFamily = "mass" | "volume" | "spoon_small" | "spoon_big" | "count" | "other";

export interface NormalizedUnit {
  family: UnitFamily;
  /** Factor to base unit of family (g, ml, tsp, tbsp, pcs). */
  factor: number;
  /** Unidad original normalizada (lowercase, sin puntos). */
  raw: string;
}

const MASS: Record<string, number> = {
  g: 1,
  gr: 1,
  gramo: 1,
  gramos: 1,
  gram: 1,
  grams: 1,
  kg: 1000,
  kilo: 1000,
  kilos: 1000,
  kilogramo: 1000,
  kilogramos: 1000,
  mg: 0.001,
};

const VOLUME: Record<string, number> = {
  ml: 1,
  mililitro: 1,
  mililitros: 1,
  cl: 10,
  dl: 100,
  l: 1000,
  litro: 1000,
  litros: 1000,
  liter: 1000,
  liters: 1000,
  taza: 250,
  tazas: 250,
  cup: 250,
  cups: 250,
};

const SPOON_SMALL = new Set(["cdta", "cdta.", "cucharadita", "cucharaditas", "tsp"]);
const SPOON_BIG = new Set(["cda", "cda.", "cucharada", "cucharadas", "tbsp"]);
const COUNT = new Set(["ud", "uds", "u", "unidad", "unidades", "pieza", "piezas", "pcs", "pc", ""]);

/** Normalizes ingredient name to use as aggregation key. */
export function normalizeIngredientName(name: string): string {
  return name.trim().toLowerCase().replaceAll(/\s+/gu, " ");
}

/** Classifies freeform unit into canonical family + factor to base. */
export function normalizeUnit(unit: string | null | undefined): NormalizedUnit {
  const raw = (unit ?? "").trim().toLowerCase().replace(/\.$/u, "");
  const massFactor = MASS[raw];
  if (massFactor !== undefined) {
    return { family: "mass", factor: massFactor, raw };
  }
  const volumeFactor = VOLUME[raw];
  if (volumeFactor !== undefined) {
    return { family: "volume", factor: volumeFactor, raw };
  }
  if (SPOON_SMALL.has(raw)) {
    return { family: "spoon_small", factor: 1, raw };
  }
  if (SPOON_BIG.has(raw)) {
    return { family: "spoon_big", factor: 1, raw };
  }
  if (COUNT.has(raw)) {
    return { family: "count", factor: 1, raw };
  }
  // Unknown unit: "other" family identified by exact text —
  // two ingredients with the same unknown unit do sum with each other.
  return { family: "other", factor: 1, raw };
}

/** Family key for aggregation ("other" families are separated by text). */
export function unitFamilyKey(n: NormalizedUnit): string {
  return n.family === "other" ? `other:${n.raw}` : n.family;
}

const ROUND_DECIMALS = 2;
const ROUNDING = 10 ** ROUND_DECIMALS;
const KILO = 1000;

function round(value: number): number {
  return Math.round(value * ROUNDING) / ROUNDING;
}

export interface DisplayQuantity {
  quantity: number;
  unit: string | null;
}

/**
 * Presents a quantity in base unit with the most readable unit:
 * ≥1000 g → kg, ≥1000 ml → l; remainder as is.
 */
export function toDisplayQuantity(
  baseQuantity: number,
  family: UnitFamily,
  rawUnit: string,
): DisplayQuantity {
  if (family === "mass") {
    return baseQuantity >= KILO
      ? { quantity: round(baseQuantity / KILO), unit: "kg" }
      : { quantity: round(baseQuantity), unit: "g" };
  }
  if (family === "volume") {
    return baseQuantity >= KILO
      ? { quantity: round(baseQuantity / KILO), unit: "l" }
      : { quantity: round(baseQuantity), unit: "ml" };
  }
  if (family === "spoon_small") {
    return { quantity: round(baseQuantity), unit: "cdta" };
  }
  if (family === "spoon_big") {
    return { quantity: round(baseQuantity), unit: "cda" };
  }
  if (family === "count") {
    return { quantity: round(baseQuantity), unit: rawUnit === "" ? null : "ud" };
  }
  return { quantity: round(baseQuantity), unit: rawUnit || null };
}
